<template>
  <div class="book-detail-page d-flex flex-column min-vh-100">
    <Header />

    <main class="flex-grow-1">

      <!-- Loading State -->
      <div v-if="isLoading" class="container py-5">
        <div class="row g-4">
          <div class="col-md-4 col-lg-3">
            <div class="skeleton-item" style="height:420px;border-radius:16px"></div>
          </div>
          <div class="col-md-8 col-lg-9">
            <div class="skeleton-item skeleton-title mb-3" style="height:36px;width:65%"></div>
            <div class="skeleton-item mb-2" style="height:20px;width:40%"></div>
            <div class="skeleton-item mb-4" style="height:20px;width:30%"></div>
            <div class="skeleton-item mb-2" style="height:14px;width:90%"></div>
            <div class="skeleton-item mb-2" style="height:14px;width:80%"></div>
            <div class="skeleton-item mb-2" style="height:14px;width:85%"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="container py-5 text-center">
        <icon icon="fa-solid fa-triangle-exclamation" class="display-1 text-danger mb-3" />
        <h3 class="font-serif fw-bold text-danger">{{ errorMessage }}</h3>
        <button @click="goBack()" class="btn-back mt-3">
          <icon icon="fa-solid fa-arrow-left" class="me-2" />Quay lại
        </button>
      </div>

      <!-- Book Detail Content -->
      <div v-else-if="book" class="book-detail-wrapper animate-fade-in">

        <!-- Hero Banner -->
        <div class="detail-hero-banner position-relative overflow-hidden">
          <div
            class="hero-blur-bg"
            :style="book.cover ? { backgroundImage: 'url(' + book.cover + ')' } : {}"
          ></div>
          <div class="hero-overlay"></div>
          <div class="container position-relative py-5">
            <div class="row align-items-end g-4">

              <!-- Book Cover -->
              <div class="col-auto">
                <div class="detail-cover-wrapper">
                  <img
                    v-if="book.cover"
                    :src="book.cover"
                    :alt="book.name"
                    class="detail-cover-img"
                    @error="book.cover = null"
                  />
                  <div
                    v-else
                    :class="['detail-cover-img fallback-book-cover d-flex flex-column justify-content-between p-3', getFallbackClass()]"
                  >
                    <icon icon="fa-solid fa-book-open" class="text-white-50 fs-2 mb-2" />
                    <span class="fallback-book-title font-serif fs-7">{{ book.name }}</span>
                  </div>
                </div>
              </div>

              <!-- Book Meta + Progress -->
              <div class="col">
                <div class="detail-meta text-white">
                  <h1 class="detail-book-title font-serif fw-bold mb-2">{{ book.name }}</h1>

                  <div class="detail-hero-meta">
                    <div class="meta-row meta-author">
                      <span class="meta-icon"><icon icon="fa-solid fa-pen-nib" class="text-warning-gold" /></span>
                      <span class="meta-text fw-semibold text-white">
                        <template v-if="book.authors && book.authors.length > 0">
                          <template v-for="(author, idx) in book.authors" :key="author.id || idx">
                            <span class="bk-tag-link text-white" style="cursor:pointer;" @click.stop="goSearchAuthor(author)">{{ author.name }}</span>
                            <span v-if="idx < book.authors.length - 1">, </span>
                          </template>
                        </template>
                        <span v-else>{{ getAuthorsString() }}</span>
                      </span>
                    </div>

                    <div v-if="book.translators && book.translators.length > 0" class="meta-row meta-translator">
                      <span class="meta-icon"><icon icon="fa-solid fa-language" class="text-white-50" /></span>
                      <span class="meta-text"><span class="meta-label">Dịch giả:</span> {{ getTranslatorsString() }}</span>
                    </div>

                    <div v-if="getCategoriesList().length > 0" class="meta-row meta-category">
                      <span class="meta-icon"><icon icon="fa-solid fa-tags" class="text-white-50" /></span>
                      <span class="meta-text">
                        <span class="meta-label">Thể loại:</span>
                        <template v-if="getCategoryObjects().length > 0">
                          <template v-for="(cat, idx) in getCategoryObjects()" :key="cat.id || idx">
                            <span class="bk-tag-link text-white-50" style="cursor:pointer;" @click.stop="goSearchCategory(cat)">{{ cat.name }}</span>
                            <span v-if="idx < getCategoryObjects().length - 1">, </span>
                          </template>
                        </template>
                        <span v-else>{{ getCategoriesList().join(', ') }}</span>
                      </span>
                    </div>
                  </div>

                  <!-- Mobile rating inline (only show on iPhone) -->
                  <div class="hero-rating-mobile d-md-none">
                    <icon icon="fa-solid fa-star" class="hero-rating-star-icon" />
                    <span class="hero-rating-value">{{ book.evaluate ? book.evaluate.toFixed(1) : '0.0' }}</span>
                    <span class="hero-rating-stat">{{ book.nov?.toLocaleString() }} lượt đánh giá</span>
                    <span class="hero-rating-stat">{{ book.nor?.toLocaleString() }} lượt review</span>
                  </div>

                  <!-- Progress Button — moved left under translators -->
                  <button @click="openBookNoteModal" :class="['btn-hero-note-trigger-v2', 'mt-3', 'status-' + (bookNote ? bookNote.progressStatus : 0)]">
                    <icon :icon="progressButtonIcon" class="me-2" />
                    <span>{{ progressButtonText }}</span>
                  </button>
                </div>
              </div>

              <!-- Rating Box on Right (desktop only) -->
              <div class="d-none d-md-block col-md-auto">
                <div class="hero-rating-box">
                  <div class="hero-rating-score">
                    <icon icon="fa-solid fa-star" class="hero-rating-star-icon" />
                    <span class="hero-rating-value">{{ book.evaluate ? book.evaluate.toFixed(1) : '0.0' }}</span>
                  </div>
                  <div class="hero-rating-divider"></div>
                  <div class="hero-rating-stat">{{ book.nov?.toLocaleString() }} lượt đánh giá</div>
                  <div class="hero-rating-stat">{{ book.nor?.toLocaleString() }} lượt review</div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Detail Body -->
        <div class="container py-5">
          <div class="row g-5">

            <!-- LEFT: Description + Rating Breakdown -->
            <div class="col-lg-8">

              <!-- Book Description -->
              <div class="detail-section mb-4">
                <h2 class="detail-section-title font-serif fw-bold mb-3">
                  <icon icon="fa-solid fa-align-left" class="me-2 text-primary-color" />
                  Nội dung sách
                </h2>
                <div class="detail-description" :class="{ 'collapsed': !showFullContent }">
                  <p class="text-muted lh-lg">{{ book.content || 'Chưa có mô tả cho cuốn sách này.' }}</p>
                </div>
                <button
                  v-if="book.content && book.content.length > 300"
                  @click="showFullContent = !showFullContent"
                  class="btn-toggle-content mt-2"
                >
                  {{ showFullContent ? 'Thu gọn' : 'Xem thêm' }}
                  <icon :icon="showFullContent ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="ms-1" />
                </button>
              </div>



              <!-- Quick Info (Mobile/Tablet only) -->
              <div class="detail-section mb-4 d-lg-none">
                <h3 class="fs-6 fw-bold font-serif mb-3 text-primary-color">Thông tin sách</h3>
                <ul class="info-list list-unstyled m-0">
                  <li v-if="book.authors && book.authors.length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Tác giả</span>
                    <div class="info-name-chips">
                      <span v-for="author in book.authors" :key="author.id" class="info-name-chip author-chip" style="cursor:pointer;" @click="goSearchAuthor(author)">
                        {{ author.name }}
                      </span>
                    </div>
                  </li>
                  <li v-if="book.translators && book.translators.length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Dịch giả</span>
                    <div class="info-name-chips">
                      <span v-for="translator in book.translators" :key="translator.id" class="info-name-chip translator-chip">
                        {{ translator.name }}
                      </span>
                    </div>
                  </li>
                  <li v-if="getCategoriesList().length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Thể loại</span>
                    <div class="info-name-chips">
                      <span v-for="(cat, idx) in getCategoryObjects()" :key="idx" class="info-name-chip category-chip-info" style="cursor:pointer;" @click="goSearchCategory(cat)">
                        {{ cat.name }}
                      </span>
                    </div>
                  </li>
                </ul>

                <button @click="goBack()" class="btn-back-card w-100 mt-4">
                  <icon icon="fa-solid fa-arrow-left" class="me-2" />Quay lại
                </button>

                <!-- Old Book Link Button (Mobile/Tablet) -->
                <router-link
                  v-if="hasOldBooks"
                  :to="'/old-books/book/' + $route.params.id"
                  class="btn-old-book-link w-100 mt-3 d-flex justify-content-center"
                >
                  <icon icon="fa-solid fa-book-open" class="me-2" />
                  <span>Xem sách cũ</span>
                  <span class="old-book-count-badge">{{ oldBookCount }}</span>
                </router-link>

                <!-- Shopee Affiliate Button (Mobile/Tablet) -->
                <a
                  v-if="book.linkShopee"
                  :href="book.linkShopee"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn-shopee-affiliate w-100 mt-3 d-flex justify-content-center"
                >
                  <svg class="shopee-logo-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1a5.25 5.25 0 0 0-5.224 5.78H4.5A1.5 1.5 0 0 0 3 8.28v12A1.5 1.5 0 0 0 4.5 21.78h15a1.5 1.5 0 0 0 1.5-1.5v-12a1.5 1.5 0 0 0-1.5-1.5h-2.276A5.25 5.25 0 0 0 12 1zm0 1.5a3.75 3.75 0 0 1 3.718 4.28H8.282A3.75 3.75 0 0 1 12 2.5zM4.5 8.28H19.5v12H4.5v-12zm7.5 2.25a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0 1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"/>
                  </svg>
                  <span>Mua trên Shopee</span>
                  <icon icon="fa-solid fa-arrow-up-right-from-square" class="shopee-ext-icon" />
                </a>
              </div>

              <!-- Suggested / Related Books (Mobile/Tablet) -->
              <div class="detail-section mb-4 d-lg-none" v-if="relatedBooks && relatedBooks.length > 0">
                <div class="mb-3">
                  <h3 class="fs-6 fw-bold font-serif m-0 text-primary-color">Tác phẩm cùng chủ đề</h3>
                </div>
                <div class="related-scroll-track">
                  <router-link
                    v-for="rel in relatedBooks"
                    :key="rel.id"
                    :to="'/book/' + rel.id"
                    class="related-scroll-item"
                  >
                    <div class="related-scroll-cover-wrap">
                      <img
                        :src="formatCoverUrl(rel.cover)"
                        :alt="rel.name"
                        class="related-scroll-img"
                        loading="lazy"
                      />
                    </div>
                    <h4 class="related-scroll-name" :title="rel.name">{{ rel.name }}</h4>
                    <div class="related-scroll-author" v-if="formatRelatedAuthors(rel.authorsStr)">
                      {{ formatRelatedAuthors(rel.authorsStr) }}
                    </div>
                    <div class="related-score mt-1">
                      <icon icon="fa-solid fa-star" class="star-mini" />
                      <span>{{ (rel.evaluate !== null && rel.evaluate !== undefined) ? rel.evaluate.toFixed(1) : '0.0' }}</span>
                    </div>
                  </router-link>
                </div>
              </div>

              <!-- Star Rating Breakdown -->
              <div class="detail-section">
                <h2 class="detail-section-title fw-bold mb-4">
                  Lượt đánh giá
                </h2>

                <div class="rating-panel">
                  <!-- Left: Big Score -->
                  <div class="rating-score-block">
                    <div class="rating-big-number">{{ book.evaluate ? book.evaluate.toFixed(1) : '0.0' }}</div>
                    <div class="rating-total-label">{{ book.nov?.toLocaleString() }} lượt đánh giá</div>
                  </div>

                  <!-- Divider -->
                  <div class="rating-divider"></div>

                  <!-- Right: Bar Chart -->
                  <div class="rating-bars-block">
                    <div v-for="item in starBars" :key="item.star" class="rbar-row">
                      <strong class="rbar-label">{{ item.star }}</strong>
                      <icon icon="fa-solid fa-star" class="rbar-star" />
                      <div class="rbar-track">
                        <div class="rbar-fill" :style="{ width: getStarPercent(item.count) + '%' }"></div>
                      </div>
                      <div class="rbar-meta">
                        <strong class="rbar-count-num">{{ item.count }}</strong>
                        <span class="rbar-pct">({{ getStarPercent(item.count) }}%)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- User Star Rating -->
                <div class="user-rating-section mt-3">
                  <span class="user-rating-label">
                    {{ isLoggedIn ? (userRating ? 'Đánh giá của bạn:' : 'Chọn sao để đánh giá:') : 'Đăng nhập để đánh giá:' }}
                  </span>
                  <div class="rating-stars-interactive-wrap" style="justify-content:flex-start;margin:0;">
                    <button
                      v-for="s in 5"
                      :key="s"
                      class="star-interactive-btn"
                      @mouseover="hoverRating = s"
                      @mouseleave="hoverRating = 0"
                      @click="isLoggedIn ? submitRating(s) : $router.push('/login')"
                      :disabled="isSubmittingRating"
                      :title="isLoggedIn ? 'Đánh giá ' + s + ' sao' : 'Đăng nhập để đánh giá'"
                    >
                      <icon
                        icon="fa-solid fa-star"
                        class="star-interactive-icon"
                        :class="{
                          'star-active': isLoggedIn ? s <= (hoverRating || userRating) : s <= hoverRating,
                          'star-inactive': isLoggedIn ? s > (hoverRating || userRating) : s > hoverRating
                        }"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Reviews Section -->
              <div id="reviews-section" class="detail-section mt-4 animate-fade-in">

                <!-- Section Header -->
                <div class="reviews-section-header">
                  <div class="reviews-header-left">
                    <h2 class="reviews-section-title fw-bold">Nhận xét của độc giả</h2>
                    <span class="reviews-count-chip">{{ reviewsTotalElements + (myReview ? 1 : 0) }} đánh giá</span>
                  </div>
                  <div class="reviews-header-right">
                    <div v-if="!isLoggedIn" class="write-review-btn-wrapper">
                      <!-- Not logged in: redirect to login -->
                      <button
                        @click="$router.push('/login')"
                        class="btn-toggle-write-review"
                      >
                        <icon icon="fa-solid fa-pen" class="me-1.5" />
                        Đăng nhập để nhận xét
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Write Review Form -->
                <!-- Hiển thị khi đã đăng nhập và chưa có nhận xét cá nhân; đã nhận xét rồi thì ẩn -->
                <div v-if="isLoggedIn && !myReview" class="review-compose-area mb-4 animate-fade-in">
                  <div class="review-form-card">
                    <div class="form-title-row">
                      <icon icon="fa-solid fa-pen-fancy" class="form-title-icon" />
                      <span class="form-title-text">Viết nhận xét của bạn</span>
                      <span class="edit-char-count ms-auto">{{ userReview.content ? userReview.content.length : 0 }} / 2000</span>
                    </div>
                    <form @submit.prevent="submitReview" class="review-actual-form">
                      <!-- Selected Sticker Preview -->
                      <div v-if="selectedStickersMap['writeReview'] && selectedStickersMap['writeReview'].length > 0" class="d-block mb-2">
                        <div v-for="(sticker, sIndex) in selectedStickersMap['writeReview']" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                          <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                          <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                            <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                          </span>
                          <button type="button" @click="removeSelectedSticker('writeReview', sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                            <icon icon="fa-solid fa-xmark" />
                          </button>
                        </div>
                      </div>
                      <textarea
                        v-model="userReview.content"
                        class="review-textarea"
                        rows="3"
                        placeholder="Hãy chia sẻ cảm nhận chân thực của bạn..."
                        maxlength="2000"
                      ></textarea>
                      <div class="form-options-row">
                        <button type="button" class="btn-emoji-trigger" title="Thêm biểu cảm & nhãn dán hài hước" @click.stop="toggleEmojiPicker('writeReview', $event)">
                          <icon icon="fa-solid fa-face-smile" class="text-warning me-1.5" />
                          <span>Biểu cảm</span>
                        </button>
                        <div class="d-flex align-items-center gap-3 ms-auto">
                          <div class="switch-option">
                            <label class="switch-container">
                              <input type="checkbox" v-model="userReview.isSpoil" />
                              <span class="switch-slider"></span>
                            </label>
                            <span class="switch-label spoiler-label">Nhận xét có Spoiler</span>
                          </div>
                          <button type="submit" class="btn-submit-review" :disabled="isSubmitting">
                            <icon v-if="isSubmitting" icon="fa-solid fa-spinner" class="fa-spin spinner-submit" />
                            <icon v-else icon="fa-solid fa-paper-plane" />
                            <span>{{ isSubmitting ? 'Đang gửi...' : 'Đăng nhận xét' }}</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <!-- BLOCK A: MY REVIEW — pinned, independent from community pagination -->
                <div v-if="isLoggedIn && myReview" class="my-review-section mb-4">
                  <div class="my-review-card" :id="'review-' + myReview.id" :data-review-id="myReview.id">
                    <!-- Card header: label + action buttons -->
                    <div class="my-review-card-header">
                      <div class="d-flex align-items-center gap-2">
                        <span class="my-review-card-label">
                          <icon icon="fa-solid fa-user-pen" class="me-1" style="font-size:0.7rem;" />Nhận xét của bạn
                        </span>
                        <div class="ms-auto d-flex align-items-center gap-2">
                          <button v-if="!isEditingMyReview" @click="editReview(myReview)" class="btn-my-review-edit flex-shrink-0">
                            <icon icon="fa-solid fa-pen" class="me-1" />Chỉnh sửa
                          </button>
                          <button v-if="!isEditingMyReview" @click="confirmDeleteReview(myReview.id)" class="btn-my-review-delete flex-shrink-0" title="Xóa nhận xét">
                            <icon icon="fa-solid fa-trash-can" class="me-1" />Xóa
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Reviewer identity row -->
                    <div class="d-flex align-items-center gap-3 my-review-identity">
                      <div class="review-entry-avatar flex-shrink-0" :style="getAvatarGradientStyle(myReview.reviewerName)">
                        <img v-if="getAvatarSrc(myReview.avatar)" :src="getAvatarSrc(myReview.avatar)" alt="Avatar" class="avatar-img" referrerpolicy="no-referrer" />
                        <template v-else>{{ myReview.reviewerName ? myReview.reviewerName.charAt(0).toUpperCase() : 'U' }}</template>
                      </div>
                      <div class="my-review-identity-info">
                        <div class="d-flex align-items-center flex-wrap gap-1">
                          <strong class="review-entry-name text-break">{{ myReview.reviewerName }}</strong>
                          <span v-if="myReview.reviewerRating > 0" class="review-rating-stars-mini" :title="'Đã đánh giá ' + myReview.reviewerRating + '/5 sao'">
                            <icon v-for="s in 5" :key="s" icon="fa-solid fa-star" class="mini-star-icon" :class="{ 'mini-star-filled': s <= myReview.reviewerRating }" />
                          </span>
                        </div>
                        <span v-if="myReview.createTime" class="review-time fs-9 text-muted">
                          {{ formatCommentTime(myReview.createTime) }}
                          <span v-if="myReview.isEdited || myReview.edited" class="is-edited-badge ms-1.5">(Đã chỉnh sửa)</span>
                        </span>
                      </div>
                    </div>

                    <!-- Content - Editing mode -->
                    <transition name="edit-slide">
                      <div v-if="isEditingMyReview" class="my-review-edit-inline mt-2">
                        <div class="edit-card-inner">
                          <div class="edit-header-row">
                            <icon icon="fa-solid fa-pen-fancy" class="edit-header-icon" />
                            <span class="edit-header-label">Chỉnh sửa nhận xét</span>
                            <span class="edit-char-count ms-auto">{{ editingReviewContent?.length || 0 }} / 2000</span>
                          </div>
                          <!-- Selected Sticker Preview -->
                          <div v-if="selectedStickersMap['editReview'] && selectedStickersMap['editReview'].length > 0" class="d-block mb-2">
                            <div v-for="(sticker, sIndex) in selectedStickersMap['editReview']" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                              <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                              <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                              </span>
                              <button type="button" @click="removeSelectedSticker('editReview', sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                <icon icon="fa-solid fa-xmark" />
                              </button>
                            </div>
                          </div>
                          <textarea
                            v-model="editingReviewContent"
                            class="edit-textarea"
                            rows="3"
                            placeholder="Chỉnh sửa nội dung nhận xét của bạn..."
                            maxlength="2000"
                          ></textarea>
                          <div class="edit-footer-row d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <button type="button" class="btn-emoji-trigger" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('editReview', $event)">
                              <icon icon="fa-solid fa-face-smile" class="text-warning me-1.5" />
                              <span>Biểu cảm</span>
                            </button>
                            <div class="d-flex align-items-center gap-3 ms-auto">
                              <div class="switch-option">
                                <label class="switch-container">
                                  <input type="checkbox" v-model="editingReviewIsSpoil" />
                                  <span class="switch-slider"></span>
                                </label>
                                <span class="switch-label spoiler-label">Nhận xét có Spoiler</span>
                              </div>
                              <div class="d-flex gap-2">
                                <button @click="cancelEditReview" class="btn-edit-cancel" :disabled="isSubmitting">
                                  <icon icon="fa-solid fa-xmark" />
                                  <span>Hủy</span>
                                </button>
                                <button @click="updateReview" class="btn-edit-save" :disabled="isSubmitting || !isReviewChanged()">
                                  <icon v-if="isSubmitting" icon="fa-solid fa-spinner" class="fa-spin" />
                                  <icon v-else icon="fa-solid fa-check" />
                                  <span>{{ isSubmitting ? 'Đang lưu...' : 'Lưu thay đổi' }}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </transition>
                    <!-- Content - Display mode -->
                    <div v-if="!isEditingMyReview">
                      <div v-if="myReview.isSpoil && !isSpoilerRevealed(myReview.id)" class="spoil-guard mt-2" @click="toggleSpoiler(myReview.id)" role="button" tabindex="0">
                        <icon icon="fa-solid fa-lock" class="spoil-guard-icon" />
                        <span>Nội dung có spoiler — click để xem</span>
                      </div>
                      <div v-else class="my-review-content-wrap">
                        <div v-if="myReview.isSpoil" class="my-review-spoil-corner">
                          <span class="rbadge rbadge-spoil"><icon icon="fa-solid fa-eye-slash" /> Spoiler</span>
                          <button @click="toggleSpoiler(myReview.id)" class="btn-hide-again">Ẩn lại</button>
                        </div>
                        <p class="my-review-text" v-html="formatContentHtml(myReview.content)"></p>
                        <div v-if="myReview.readAgain" class="my-review-content-badges mt-1">
                          <span class="rbadge rbadge-reread"><icon icon="fa-solid fa-arrows-rotate" /> Đọc lại</span>
                        </div>
                      </div>
                    </div>

                    <!-- Actions footer -->
                    <div class="my-review-actions">
                      <div class="tooltip-wrapper">
                        <button @click="interactReview(myReview, true)" class="btn-review-action btn-like-toggle" :class="{ 'active': myReview.userInteract === 1 }">
                          <icon icon="fa-solid fa-thumbs-up" /><span>{{ myReview.likeCount || 0 }}</span>
                        </button>
                        <span class="custom-tooltip-text">Thích</span>
                      </div>
                      <div class="tooltip-wrapper">
                        <button @click="interactReview(myReview, false)" class="btn-review-action btn-dislike-toggle" :class="{ 'active': myReview.userInteract === 0 }">
                          <icon icon="fa-solid fa-thumbs-down" /><span>{{ myReview.dislikeCount || 0 }}</span>
                        </button>
                        <span class="custom-tooltip-text">Không thích</span>
                      </div>
                      <div class="tooltip-wrapper">
                        <button @click="toggleComments(myReview.id)" class="btn-review-action btn-comment-toggle" :class="{ 'active': showCommentsMap[myReview.id] }">
                          <icon icon="fa-solid fa-comments" /><span>{{ myReview.commentCount || 0 }}</span>
                        </button>
                        <span class="custom-tooltip-text">Bình luận</span>
                      </div>
                      <div class="tooltip-wrapper">
                        <button @click="copyReviewUrl(myReview.id)" class="btn-review-action btn-share-review" title="Sao chép liên kết nhận xét">
                          <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                        </button>
                        <span class="custom-tooltip-text">Sao chép liên kết</span>
                      </div>
                    </div>

                    <!-- Comments -->
                    <div v-if="showCommentsMap[myReview.id]" :data-review-comments="myReview.id" class="review-comments-box mt-3 animate-fade-in">
                      <div class="comments-drawer-header d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center gap-2">
                          <icon icon="fa-solid fa-comments" class="drawer-icon" />
                          <span>Bình luận & Thảo luận</span>
                        </div>
                        <button class="btn btn-sm btn-outline-secondary btn-expand-comments" @click="openReviewFocusModal(myReview)" title="Mở rộng">
                          <icon icon="fa-solid fa-expand" />
                        </button>
                      </div>
                      <div class="comments-scroll-area">
                      <div v-if="isCommentsLoadingMap[myReview.id] && (!commentsMap[myReview.id] || commentsMap[myReview.id].length === 0)" class="comments-loading text-center py-2">
                        <icon icon="fa-solid fa-spinner" class="fa-spin text-muted" />
                      </div>
                      <div v-else class="comments-list">
                        <div v-if="!commentsMap[myReview.id] || commentsMap[myReview.id].length === 0" class="comment-item-empty text-muted py-2 fs-8 text-center">
                          Chưa có bình luận nào. Hãy gửi bình luận đầu tiên!
                        </div>
                        <div v-else v-for="comment in commentsMap[myReview.id]" :key="comment.id" :id="'comment-' + comment.id" :data-comment-id="comment.id" :data-user-id="comment.userId" class="comment-item">
                          <div class="comment-avatar" :style="getAvatarGradientStyle(comment.userName)">
                            <img v-if="getAvatarSrc(comment.avatar)" :src="getAvatarSrc(comment.avatar)" alt="Avatar" class="avatar-img" />
                            <template v-else>{{ comment.userName ? comment.userName.charAt(0).toUpperCase() : 'U' }}</template>
                          </div>
                          <div class="comment-body">
                            <div class="comment-header-row">
                              <div class="comment-header-info">
                                <strong class="comment-author fs-8 text-dark">{{ comment.userName }}</strong>
                                <span class="comment-meta-inline">
                                  <span class="comment-time fs-9 text-muted">{{ formatCommentTime(comment.createTime) }}</span>
                                  <span v-if="comment.isEdited || comment.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                </span>
                              </div>
                              <div v-if="isLoggedIn && currentUserId && String(comment.userId) === String(currentUserId) && !isEditingCommentMap[comment.id]" class="comment-top-right-actions">
                                <button @click="editComment(comment)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                  <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                </button>
                                <button @click="confirmDeleteComment(myReview.id, comment.id)" class="btn-comment-action btn-delete-comment" title="Xóa bình luận">
                                  <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                </button>
                              </div>
                            </div>
                            <p v-if="!isEditingCommentMap[comment.id]" class="comment-content m-0 fs-8-5 text-secondary" v-html="formatContentHtml(comment.content)"></p>
                            <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                              <div v-if="selectedStickersMap['editComment_' + comment.id] && selectedStickersMap['editComment_' + comment.id].length > 0" class="d-block mb-2">
                              <div v-for="(sticker, sIndex) in selectedStickersMap['editComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                  <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                </span>
                                <button type="button" @click="removeSelectedSticker('editComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                  <icon icon="fa-solid fa-xmark" />
                                </button>
                              </div>
                            </div>
                              <textarea
                                v-model="editingCommentMap[comment.id]"
                                :data-edit-comment-input="comment.id"
                                class="comment-edit-input"
                                rows="2"
                                placeholder="Nhập nội dung bình luận..."
                                maxlength="500"
                              ></textarea>
                              <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                <span class="fs-9 text-muted">{{ (editingCommentMap[comment.id] || '').length }}/500</span>
                                <div class="d-flex gap-2">
                                  <button type="button" @click="cancelEditComment(comment.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[comment.id]">Hủy</button>
                                  <button type="button" @click="updateCommentContent(myReview ? myReview.id : review.id, comment)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[comment.id] || !isCommentChanged(comment)">
                                    <icon v-if="isSubmittingEditCommentMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                    <span>Lưu</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div v-if="!isEditingCommentMap[comment.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                              <button @click="interactComment(comment, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': comment.userInteract === 1 }" title="Thích">
                                <icon icon="fa-solid fa-thumbs-up" /><span>{{ comment.likeCount || 0 }}</span>
                              </button>
                              <button @click="interactComment(comment, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': comment.userInteract === 0 }" title="Không thích">
                                <icon icon="fa-solid fa-thumbs-down" /><span>{{ comment.dislikeCount || 0 }}</span>
                              </button>
                              <button @click="toggleReplyForm(myReview.id, comment.id)" class="btn-comment-action btn-reply-comment" :class="{ 'active': showReplyFormsMap[comment.id] }" title="Trả lời">
                                <icon icon="fa-solid fa-reply" /><span>Trả lời</span>
                              </button>
                              <button @click="copyCommentUrl(myReview.id, comment.id, 'COMMENT')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết bình luận">
                                <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                              </button>
                            </div>
                            <!-- Nested replies toggle (inside comment-body) -->
                            <div v-if="comment.replies && comment.replies.length > 0" class="comment-replies-toggle">
                              <button @click="toggleReplies(comment.id)" class="btn-toggle-replies">
                                <icon :icon="showRepliesMap[comment.id] ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="toggle-reply-icon" />
                                <span>{{ showRepliesMap[comment.id] ? 'Ẩn' : 'Xem' }} {{ comment.replies.length }} câu trả lời</span>
                              </button>
                            </div>
                            <!-- Nested replies & reply form thread container (inside comment-body) -->
                            <div v-if="(showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0) || showReplyFormsMap[comment.id]" class="comment-replies" :data-replies-list="comment.id">
                              <!-- Existing replies list -->
                              <div v-if="showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0" class="comment-replies-list">
                                  <div v-for="reply in comment.replies" :key="reply.id" :id="'comment-' + reply.id" :data-reply-id="reply.id" class="comment-reply-item" :data-reply-user-id="reply.userId">  
                                  <div class="comment-reply-avatar" :style="getAvatarGradientStyle(reply.userName)">
                                    <img v-if="getAvatarSrc(reply.avatar)" :src="getAvatarSrc(reply.avatar)" alt="Avatar" class="avatar-img" />
                                    <template v-else>{{ reply.userName ? reply.userName.charAt(0).toUpperCase() : 'U' }}</template>
                                  </div>
                                  <div class="comment-reply-body">
                                    <div class="comment-header-row">
                                      <div class="comment-header-info">
                                        <strong class="comment-author text-dark">{{ reply.userName }}</strong>
                                        <span class="comment-meta-inline">
                                          <span class="comment-time text-muted">{{ formatCommentTime(reply.createTime) }}</span>
                                          <span v-if="reply.isEdited || reply.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                        </span>
                                      </div>
                                      <div v-if="isLoggedIn && currentUserId && String(reply.userId) === String(currentUserId) && !isEditingCommentMap[reply.id]" class="comment-top-right-actions">
                                        <button @click="editComment(reply)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                          <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                        </button>
                                        <button @click="confirmDeleteComment(myReview.id, reply.id)" class="btn-comment-action btn-delete-comment" title="Xóa câu trả lời">
                                          <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                        </button>
                                      </div>
                                    </div>
                                    <p v-if="!isEditingCommentMap[reply.id]" class="comment-content m-0 text-secondary" v-html="formatContentHtml(reply.content)"></p>
                                    <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                                      <textarea
                                        v-model="editingCommentMap[reply.id]"
                                        :data-edit-comment-input="reply.id"
                                        class="comment-edit-input"
                                        rows="2"
                                        placeholder="Nhập câu trả lời..."
                                        maxlength="500"
                                      ></textarea>
                                      <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                        <span class="fs-9 text-muted">{{ (editingCommentMap[reply.id] || '').length }}/500</span>
                                        <div class="d-flex gap-2">
                                          <button type="button" @click="cancelEditComment(reply.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[reply.id]">Hủy</button>
                                          <button type="button" @click="updateCommentContent(myReview.id, reply)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[reply.id] || !isCommentChanged(reply)">
                                            <icon v-if="isSubmittingEditCommentMap[reply.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                            <span>Lưu</span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    <div v-if="!isEditingCommentMap[reply.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                                      <button @click="interactComment(reply, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': reply.userInteract === 1 }" title="Thích">
                                        <icon icon="fa-solid fa-thumbs-up" /><span>{{ reply.likeCount || 0 }}</span>
                                      </button>
                                      <button @click="interactComment(reply, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': reply.userInteract === 0 }" title="Không thích">
                                        <icon icon="fa-solid fa-thumbs-down" /><span>{{ reply.dislikeCount || 0 }}</span>
                                      </button>
                                      <button @click="copyCommentUrl(myReview.id, reply.id, 'REPLY')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết câu trả lời">
                                        <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <!-- Inline reply form inside the same thread container -->
                              <div v-if="showReplyFormsMap[comment.id]" class="comment-reply-form mt-2 animate-fade-in">
                                  <div v-if="selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0" class="d-block mb-2">
                                  <div v-for="(sticker, sIndex) in selectedStickersMap['replyComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                    <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                    <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                      <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                    </span>
                                    <button type="button" @click="removeSelectedSticker('replyComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                      <icon icon="fa-solid fa-xmark" />
                                    </button>
                                  </div>
                                </div>
                                  <form @submit.prevent="submitReply(myReview.id, comment.id)" class="comment-form-flex">
                                    <textarea v-model="replyInputsMap[comment.id]" :data-reply-input="comment.id" class="comment-reply-input" :placeholder="'Trả lời ' + comment.userName + '...'" maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitReply(myReview.id, comment.id))" @input="autoResizeTextarea"></textarea>
                                    <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('replyComment_' + comment.id, $event)">
                                      <icon icon="fa-solid fa-face-smile" />
                                    </button>
                                    <button type="submit" class="btn-submit-comment" :disabled="submittingReplyMap[comment.id] || (!replyInputsMap[comment.id]?.trim() && !(selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0))" title="Gửi câu trả lời">
                                      <icon v-if="submittingReplyMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                                      <icon v-else icon="fa-solid fa-paper-plane" />
                                    </button>
                                  </form>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="commentsTotalPagesMap[myReview.id] > 1" class="comments-mini-pagination d-flex justify-content-center align-items-center gap-2 mt-2 pt-2">
                        <button @click="fetchComments(myReview.id, commentsPageMap[myReview.id] - 1)" :disabled="commentsPageMap[myReview.id] === 0" class="btn-mini-pag">Trước</button>
                        <span class="fs-9 text-muted">{{ commentsPageMap[myReview.id] + 1 }} / {{ commentsTotalPagesMap[myReview.id] }}</span>
                        <button @click="fetchComments(myReview.id, commentsPageMap[myReview.id] + 1)" :disabled="commentsPageMap[myReview.id] === commentsTotalPagesMap[myReview.id] - 1" class="btn-mini-pag">Sau</button>
                      </div>
                      </div>
                      <div class="comment-form-wrapper mt-3 pt-2 border-top">
                        <div v-if="!isLoggedIn" class="comment-login-hint fs-8 text-muted">
                          Bạn cần <router-link to="/login" class="text-primary-color text-decoration-underline">đăng nhập</router-link> để gửi bình luận.
                        </div>
                        <form v-else @submit.prevent="submitComment(myReview.id)" class="d-flex flex-column gap-2 w-100">
                          <div v-if="selectedStickersMap['newComment_' + myReview.id] && selectedStickersMap['newComment_' + myReview.id].length > 0" class="d-block mb-2">
                          <div v-for="(sticker, sIndex) in selectedStickersMap['newComment_' + myReview.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                            <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                            <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                              <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                            </span>
                            <button type="button" @click="removeSelectedSticker('newComment_' + myReview.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                              <icon icon="fa-solid fa-xmark" />
                            </button>
                          </div>
                        </div>
                          <div class="comment-form-flex">
                            <textarea v-model="commentInputsMap[myReview.id]" class="comment-input-field" placeholder="Viết bình luận (tối đa 500 ký tự)..." maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitComment(myReview.id))" @input="autoResizeTextarea"></textarea>
                          <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('newComment_' + myReview.id, $event)">
                            <icon icon="fa-solid fa-face-smile" />
                          </button>
                          <button type="submit" class="btn-submit-comment" :disabled="isSubmittingCommentMap[myReview.id] || (!commentInputsMap[myReview.id]?.trim() && !(selectedStickersMap['newComment_' + myReview.id] && selectedStickersMap['newComment_' + myReview.id].length > 0))">
                            <icon v-if="isSubmittingCommentMap[myReview.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                            <icon v-else icon="fa-solid fa-paper-plane" />
                          </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                  <!-- BLOCK B: COMMUNITY REVIEWS — with pagination at bottom only -->
                <div class="community-reviews-section">
                  <!-- Always show the community header for visual separation -->
                  <div class="other-reviews-section-header">
                    <icon icon="fa-solid fa-users" class="me-2 text-muted" style="font-size:0.75rem;" />
                    <span class="fw-bold">Nhận xét từ cộng đồng</span>
                    <nav v-if="reviewsTotalPages > 1" class="ms-auto d-flex align-items-center gap-1">
                      <button @click="changeReviewsPage(reviewsPage - 1)" :disabled="reviewsPage === 0" class="rpag-btn rpag-btn-sm" aria-label="Trang trước">
                        <icon icon="fa-solid fa-chevron-left" />
                      </button>
                      <span class="fs-9 text-muted px-1">{{ reviewsPage + 1 }} / {{ reviewsTotalPages }}</span>
                      <button @click="changeReviewsPage(reviewsPage + 1)" :disabled="reviewsPage === reviewsTotalPages - 1" class="rpag-btn rpag-btn-sm" aria-label="Trang sau">
                        <icon icon="fa-solid fa-chevron-right" />
                      </button>
                    </nav>
                  </div>

                  <div v-if="isReviewsLoading" class="reviews-state-wrapper">
                    <div class="review-skeleton" v-for="i in 3" :key="i">
                      <div class="skeleton-avatar"></div>
                      <div class="skeleton-lines">
                        <div class="skeleton-line w-40"></div>
                        <div class="skeleton-line w-70"></div>
                        <div class="skeleton-line w-90"></div>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="reviewsErrorMessage" class="reviews-state-wrapper reviews-state-error">
                    <icon icon="fa-solid fa-circle-exclamation" class="state-icon" />
                    <p class="state-text">{{ reviewsErrorMessage }}</p>
                  </div>

                  <div v-else-if="otherReviews.length === 0" class="reviews-state-wrapper reviews-state-empty">
                    <icon icon="fa-solid fa-comment-dots" class="state-icon" />
                    <p class="state-text">{{ myReview ? 'Chưa có nhận xét nào từ cộng đồng.' : 'Chưa có đánh giá nào. Hãy là người đầu tiên chia sẻ!' }}</p>
                  </div>

                  <div v-else class="reviews-feed d-flex flex-column gap-3">
                    <article v-for="review in otherReviews" :key="review.id" :id="'review-' + review.id" :data-review-id="review.id" class="community-review-card">
                      <!-- Reviewer identity row -->
                      <div class="d-flex align-items-center gap-3 my-review-identity">
                        <div class="review-entry-avatar flex-shrink-0" :style="getAvatarGradientStyle(review.reviewerName)">
                          <img v-if="getAvatarSrc(review.avatar)" :src="getAvatarSrc(review.avatar)" alt="Avatar" class="avatar-img" />
                          <template v-else>{{ review.reviewerName ? review.reviewerName.charAt(0).toUpperCase() : 'U' }}</template>
                        </div>
                        <div class="my-review-identity-info">
                          <div class="d-flex align-items-center flex-wrap gap-1">
                            <strong class="review-entry-name text-break">{{ review.reviewerName }}</strong>
                            <span v-if="review.reviewerRating > 0" class="review-rating-stars-mini" :title="'Đã đánh giá ' + review.reviewerRating + '/5 sao'">
                              <icon v-for="s in 5" :key="s" icon="fa-solid fa-star" class="mini-star-icon" :class="{ 'mini-star-filled': s <= review.reviewerRating }" />
                            </span>
                          </div>
                          <span v-if="review.createTime" class="review-time fs-9 text-muted">
                            {{ formatCommentTime(review.createTime) }}
                            <span v-if="review.isEdited || review.edited" class="is-edited-badge ms-1.5">(Đã chỉnh sửa)</span>
                          </span>
                        </div>
                      </div>

                      <!-- Content -->
                      <div v-if="review.isSpoil && !isSpoilerRevealed(review.id)" class="spoil-guard mt-2" @click="toggleSpoiler(review.id)" role="button" tabindex="0">
                        <icon icon="fa-solid fa-lock" class="spoil-guard-icon" />
                        <span>Nội dung có spoiler — click để xem</span>
                      </div>
                      <div v-else class="my-review-content-wrap">
                        <div v-if="review.isSpoil" class="my-review-spoil-corner">
                          <span class="rbadge rbadge-spoil"><icon icon="fa-solid fa-eye-slash" /> Spoiler</span>
                          <button @click="toggleSpoiler(review.id)" class="btn-hide-again">Ẩn lại</button>
                        </div>
                        <p class="my-review-text" v-html="formatContentHtml(review.content)"></p>
                        <div v-if="review.readAgain" class="my-review-content-badges mt-1">
                          <span class="rbadge rbadge-reread"><icon icon="fa-solid fa-arrows-rotate" /> Đọc lại</span>
                        </div>
                      </div>

                      <!-- Actions footer -->
                      <div class="my-review-actions">
                        <div class="tooltip-wrapper">
                          <button @click="interactReview(review, true)" class="btn-review-action btn-like-toggle" :class="{ 'active': review.userInteract === 1 }">
                            <icon icon="fa-solid fa-thumbs-up" /><span>{{ review.likeCount || 0 }}</span>
                          </button>
                          <span class="custom-tooltip-text">Thích</span>
                        </div>
                        <div class="tooltip-wrapper">
                          <button @click="interactReview(review, false)" class="btn-review-action btn-dislike-toggle" :class="{ 'active': review.userInteract === 0 }">
                            <icon icon="fa-solid fa-thumbs-down" /><span>{{ review.dislikeCount || 0 }}</span>
                          </button>
                          <span class="custom-tooltip-text">Không thích</span>
                        </div>
                        <div class="tooltip-wrapper">
                          <button @click="toggleComments(review.id)" class="btn-review-action btn-comment-toggle" :class="{ 'active': showCommentsMap[review.id] }">
                            <icon icon="fa-solid fa-comments" /><span>{{ review.commentCount || 0 }}</span>
                          </button>
                          <span class="custom-tooltip-text">Bình luận</span>
                        </div>
                        <div class="tooltip-wrapper">
                          <button @click="copyReviewUrl(review.id)" class="btn-review-action btn-share-review" title="Sao chép liên kết nhận xét">
                            <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                          </button>
                          <span class="custom-tooltip-text">Sao chép liên kết</span>
                        </div>
                      </div>

                      <!-- Comments -->
                      <div v-if="showCommentsMap[review.id]" :data-review-comments="review.id" class="review-comments-box mt-3 animate-fade-in" :class="{ 'fullscreen-review': isFullscreenReviewMap && isFullscreenReviewMap[review.id] }">
                        <div class="comments-drawer-header d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center gap-2">
                          <icon icon="fa-solid fa-comments" class="drawer-icon" />
                          <span>Bình luận & Thảo luận</span>
                        </div>
                          <button class="btn btn-sm btn-outline-secondary btn-expand-comments" @click="openReviewFocusModal(review)" title="Mở rộng">
                            <icon icon="fa-solid fa-expand" />
                          </button>
                        </div>
                        <div class="comments-scroll-area">
                        <div v-if="isCommentsLoadingMap[review.id] && (!commentsMap[review.id] || commentsMap[review.id].length === 0)" class="comments-loading text-center py-2">
                          <icon icon="fa-solid fa-spinner" class="fa-spin text-muted" />
                        </div>
                        <div v-else class="comments-list">
                          <div v-if="!commentsMap[review.id] || commentsMap[review.id].length === 0" class="comment-item-empty text-muted py-2 fs-8 text-center">
                            Chưa có bình luận nào. Hãy gửi bình luận đầu tiên!
                          </div>
                          <div v-else v-for="comment in commentsMap[review.id]" :key="comment.id" :id="'comment-' + comment.id" :data-comment-id="comment.id" :data-user-id="comment.userId" class="comment-item">
                            <div class="comment-avatar" :style="getAvatarGradientStyle(comment.userName)">
                              <img v-if="getAvatarSrc(comment.avatar)" :src="getAvatarSrc(comment.avatar)" alt="Avatar" class="avatar-img" />
                              <template v-else>{{ comment.userName ? comment.userName.charAt(0).toUpperCase() : 'U' }}</template>
                            </div>
                            <div class="comment-body">
                              <div class="comment-header-row">
                                <div class="comment-header-info">
                                  <strong class="comment-author fs-8 text-dark">{{ comment.userName }}</strong>
                                  <span class="comment-meta-inline">
                                    <span class="comment-time fs-9 text-muted">{{ formatCommentTime(comment.createTime) }}</span>
                                    <span v-if="comment.isEdited || comment.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                  </span>
                                </div>
                                <div v-if="isLoggedIn && currentUserId && String(comment.userId) === String(currentUserId) && !isEditingCommentMap[comment.id]" class="comment-top-right-actions">
                                  <button @click="editComment(comment)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                    <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                  </button>
                                  <button @click="confirmDeleteComment(review.id, comment.id)" class="btn-comment-action btn-delete-comment" title="Xóa bình luận">
                                    <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                  </button>
                                </div>
                              </div>
                              <p v-if="!isEditingCommentMap[comment.id]" class="comment-content m-0 fs-8-5 text-secondary" v-html="formatContentHtml(comment.content)"></p>
                              <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                                <div v-if="selectedStickersMap['editComment_' + comment.id] && selectedStickersMap['editComment_' + comment.id].length > 0" class="d-block mb-2">
                                <div v-for="(sticker, sIndex) in selectedStickersMap['editComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                  <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                  <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                    <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                  </span>
                                  <button type="button" @click="removeSelectedSticker('editComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                    <icon icon="fa-solid fa-xmark" />
                                  </button>
                                </div>
                              </div>
                              <textarea
                                v-model="editingCommentMap[comment.id]"
                                :data-edit-comment-input="comment.id"
                                  class="comment-edit-input"
                                  rows="2"
                                  placeholder="Nhập nội dung bình luận..."
                                  maxlength="500"
                                ></textarea>
                                <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                  <span class="fs-9 text-muted">{{ (editingCommentMap[comment.id] || '').length }}/500</span>
                                  <div class="d-flex gap-2">
                                    <button type="button" @click="cancelEditComment(comment.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[comment.id]">Hủy</button>
                                    <button type="button" @click="updateCommentContent(review.id, comment)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[comment.id] || !isCommentChanged(comment)">
                                      <icon v-if="isSubmittingEditCommentMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                      <span>Lưu</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div v-if="!isEditingCommentMap[comment.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                                <button @click="interactComment(comment, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': comment.userInteract === 1 }" title="Thích">
                                  <icon icon="fa-solid fa-thumbs-up" /><span>{{ comment.likeCount || 0 }}</span>
                                </button>
                                <button @click="interactComment(comment, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': comment.userInteract === 0 }" title="Không thích">
                                  <icon icon="fa-solid fa-thumbs-down" /><span>{{ comment.dislikeCount || 0 }}</span>
                                </button>
                                <button @click="toggleReplyForm(review.id, comment.id)" class="btn-comment-action btn-reply-comment" :class="{ 'active': showReplyFormsMap[comment.id] }" title="Trả lời">
                                  <icon icon="fa-solid fa-reply" /><span>Trả lời</span>
                                </button>
                                <button @click="copyCommentUrl(review.id, comment.id, 'COMMENT')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết bình luận">
                                  <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                                </button>
                              </div>
                              <!-- Nested replies toggle (inside comment-body) -->
                              <div v-if="comment.replies && comment.replies.length > 0" class="comment-replies-toggle">
                                <button @click="toggleReplies(comment.id)" class="btn-toggle-replies">
                                  <icon :icon="showRepliesMap[comment.id] ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="toggle-reply-icon" />
                                  <span>{{ showRepliesMap[comment.id] ? 'Ẩn' : 'Xem' }} {{ comment.replies.length }} câu trả lời</span>
                                </button>
                              </div>
                              <!-- Nested replies & reply form thread container (inside comment-body) -->
                              <div v-if="(showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0) || showReplyFormsMap[comment.id]" class="comment-replies" :data-replies-list="comment.id">
                                <!-- Existing replies list -->
                                <div v-if="showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0" class="comment-replies-list">
                                  <div v-for="reply in comment.replies" :key="reply.id" :id="'comment-' + reply.id" :data-reply-id="reply.id" class="comment-reply-item" :data-reply-user-id="reply.userId">
                                    <div class="comment-reply-avatar" :style="getAvatarGradientStyle(reply.userName)">
                                      <img v-if="getAvatarSrc(reply.avatar)" :src="getAvatarSrc(reply.avatar)" alt="Avatar" class="avatar-img" />
                                      <template v-else>{{ reply.userName ? reply.userName.charAt(0).toUpperCase() : 'U' }}</template>
                                    </div>
                                    <div class="comment-reply-body">
                                      <div class="comment-header-row">
                                        <div class="comment-header-info">
                                          <strong class="comment-author text-dark">{{ reply.userName }}</strong>
                                          <span class="comment-meta-inline">
                                            <span class="comment-time text-muted">{{ formatCommentTime(reply.createTime) }}</span>
                                            <span v-if="reply.isEdited || reply.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                          </span>
                                        </div>
                                        <div v-if="isLoggedIn && currentUserId && String(reply.userId) === String(currentUserId) && !isEditingCommentMap[reply.id]" class="comment-top-right-actions">
                                          <button @click="editComment(reply)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                            <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                          </button>
                                          <button @click="confirmDeleteComment(review.id, reply.id)" class="btn-comment-action btn-delete-comment" title="Xóa câu trả lời">
                                            <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                          </button>
                                        </div>
                                      </div>
                                      <p v-if="!isEditingCommentMap[reply.id]" class="comment-content m-0 text-secondary" v-html="formatContentHtml(reply.content)"></p>
                                      <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                                        <textarea
                                          v-model="editingCommentMap[reply.id]"
                                          :data-edit-comment-input="reply.id"
                                          class="comment-edit-input"
                                          rows="2"
                                          placeholder="Nhập câu trả lời..."
                                          maxlength="500"
                                        ></textarea>
                                        <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                          <span class="fs-9 text-muted">{{ (editingCommentMap[reply.id] || '').length }}/500</span>
                                          <div class="d-flex gap-2">
                                            <button type="button" @click="cancelEditComment(reply.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[reply.id]">Hủy</button>
                                            <button type="button" @click="updateCommentContent(review.id, reply)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[reply.id] || !isCommentChanged(reply)">
                                              <icon v-if="isSubmittingEditCommentMap[reply.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                              <span>Lưu</span>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div v-if="!isEditingCommentMap[reply.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                                        <button @click="interactComment(reply, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': reply.userInteract === 1 }" title="Thích">
                                          <icon icon="fa-solid fa-thumbs-up" /><span>{{ reply.likeCount || 0 }}</span>
                                        </button>
                                        <button @click="interactComment(reply, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': reply.userInteract === 0 }" title="Không thích">
                                          <icon icon="fa-solid fa-thumbs-down" /><span>{{ reply.dislikeCount || 0 }}</span>
                                        </button>
                                        <button @click="copyCommentUrl(review.id, reply.id, 'REPLY')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết câu trả lời">
                                          <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <!-- Inline reply form inside the same thread container -->
                                <div v-if="showReplyFormsMap[comment.id]" class="comment-reply-form mt-2 animate-fade-in">
                                  <div v-if="selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0" class="d-block mb-2">
                                    <div v-for="(sticker, sIndex) in selectedStickersMap['replyComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                      <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                      <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                        <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                      </span>
                                      <button type="button" @click="removeSelectedSticker('replyComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                        <icon icon="fa-solid fa-xmark" />
                                      </button>
                                    </div>
                                  </div>
                                  <form @submit.prevent="submitReply(review.id, comment.id)" class="comment-form-flex">
                                    <textarea v-model="replyInputsMap[comment.id]" :data-reply-input="comment.id" class="comment-reply-input" :placeholder="'Trả lời ' + comment.userName + '...'" maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitReply(review.id, comment.id))" @input="autoResizeTextarea"></textarea>
                                    <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('replyComment_' + comment.id, $event)">
                                      <icon icon="fa-solid fa-face-smile" />
                                    </button>
                                    <button type="submit" class="btn-submit-comment" :disabled="submittingReplyMap[comment.id] || (!replyInputsMap[comment.id]?.trim() && !(selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0))" title="Gửi câu trả lời">
                                      <icon v-if="submittingReplyMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                                      <icon v-else icon="fa-solid fa-paper-plane" />
                                    </button>
                                  </form>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="commentsTotalPagesMap[review.id] > 1" class="comments-mini-pagination d-flex justify-content-center align-items-center gap-2 mt-2 pt-2">
                            <button @click="fetchComments(review.id, commentsPageMap[review.id] - 1)" :disabled="commentsPageMap[review.id] === 0" class="btn-mini-pag">Trước</button>
                            <span class="fs-9 text-muted">{{ commentsPageMap[review.id] + 1 }} / {{ commentsTotalPagesMap[review.id] }}</span>
                            <button @click="fetchComments(review.id, commentsPageMap[review.id] + 1)" :disabled="commentsPageMap[review.id] === commentsTotalPagesMap[review.id] - 1" class="btn-mini-pag">Sau</button>
                          </div>
                        </div>
                        </div>
                        <div class="comment-form-wrapper mt-3 pt-2 border-top">
                          <div v-if="!isLoggedIn" class="comment-login-hint fs-8 text-muted">
                            Bạn cần <router-link to="/login" class="text-primary-color text-decoration-underline">đăng nhập</router-link> để gửi bình luận.
                          </div>
                          <form v-else @submit.prevent="submitComment(review.id)" class="d-flex flex-column gap-2 w-100">
                            <div v-if="selectedStickersMap['newComment_' + review.id] && selectedStickersMap['newComment_' + review.id].length > 0" class="d-block mb-2">
                            <div v-for="(sticker, sIndex) in selectedStickersMap['newComment_' + review.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                              <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                              <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                              </span>
                              <button type="button" @click="removeSelectedSticker('newComment_' + review.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                <icon icon="fa-solid fa-xmark" />
                              </button>
                            </div>
                          </div>
                            <div class="comment-form-flex">
                              <textarea v-model="commentInputsMap[review.id]" class="comment-input-field" placeholder="Viết bình luận (tối đa 500 ký tự)..." maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitComment(review.id))" @input="autoResizeTextarea"></textarea>
                            <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('newComment_' + review.id, $event)">
                              <icon icon="fa-solid fa-face-smile" />
                            </button>
                            <button type="submit" class="btn-submit-comment" :disabled="isSubmittingCommentMap[review.id] || (!commentInputsMap[review.id]?.trim() && !(selectedStickersMap['newComment_' + review.id] && selectedStickersMap['newComment_' + review.id].length > 0))">
                              <icon v-if="isSubmittingCommentMap[review.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                              <icon v-else icon="fa-solid fa-paper-plane" />
                            </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </article>
                  </div>

                  <!-- Pagination — only for community reviews, at bottom -->
                  <nav v-if="reviewsTotalPages > 1" class="reviews-pagination mt-4" aria-label="Trang nhận xét cộng đồng">
                    <button @click="changeReviewsPage(reviewsPage - 1)" :disabled="reviewsPage === 0" class="rpag-btn" aria-label="Trang trước">
                      <icon icon="fa-solid fa-chevron-left" />
                    </button>
                    <template v-for="(page, idx) in paginationRange" :key="idx">
                      <span v-if="page === '...'" class="rpag-ellipsis">…</span>
                      <button
                        v-else
                        @click="changeReviewsPage(page)"
                        :class="['rpag-btn rpag-num', { 'rpag-active': reviewsPage === page }]"
                      >{{ page + 1 }}</button>
                    </template>
                    <button @click="changeReviewsPage(reviewsPage + 1)" :disabled="reviewsPage === reviewsTotalPages - 1" class="rpag-btn" aria-label="Trang sau">
                      <icon icon="fa-solid fa-chevron-right" />
                    </button>
                  </nav>

                </div><!-- /community-reviews-section -->

              </div><!-- /reviews-section -->

            </div>

            <!-- RIGHT: Quick Info Card & Related Books (Desktop only) -->
            <div class="col-lg-4 d-none d-lg-block">
              <div class="detail-sidebar-sticky">
                <div class="detail-info-card">
                  <h3 class="fs-6 fw-bold font-serif mb-3 text-primary-color">Thông tin sách</h3>
                  <ul class="info-list list-unstyled m-0">

                  <li v-if="book.authors && book.authors.length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Tác giả</span>
                    <div class="info-name-chips">
                      <span v-for="author in book.authors" :key="author.id" class="info-name-chip author-chip" style="cursor:pointer;" @click="goSearchAuthor(author)">
                        {{ author.name }}
                      </span>
                    </div>
                  </li>
                  <li v-if="book.translators && book.translators.length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Dịch giả</span>
                    <div class="info-name-chips">
                      <span v-for="translator in book.translators" :key="translator.id" class="info-name-chip translator-chip">
                        {{ translator.name }}
                      </span>
                    </div>
                  </li>
                  <li v-if="getCategoriesList().length > 0" class="info-item-stacked py-2">
                    <span class="text-muted fs-7 d-block mb-1">Thể loại</span>
                    <div class="info-name-chips">
                      <span v-for="(cat, idx) in getCategoryObjects()" :key="idx" class="info-name-chip category-chip-info" style="cursor:pointer;" @click="goSearchCategory(cat)">
                        {{ cat.name }}
                      </span>
                    </div>
                  </li>
                </ul>

                <button @click="goBack()" class="btn-back-card w-100 mt-4">
                  <icon icon="fa-solid fa-arrow-left" class="me-2" />Quay lại
                </button>

                <!-- Old Book Link Button (Desktop) -->
                <router-link
                  v-if="hasOldBooks"
                  :to="'/old-books/book/' + $route.params.id"
                  class="btn-old-book-link w-100 mt-3 d-none d-lg-flex justify-content-center"
                >
                  <icon icon="fa-solid fa-book-open" class="me-2" />
                  <span>Xem sách cũ</span>
                  <span class="old-book-count-badge">{{ oldBookCount }}</span>
                </router-link>

                <!-- Shopee Affiliate Button (Desktop) -->
                <a
                  v-if="book.linkShopee"
                  :href="book.linkShopee"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn-shopee-affiliate w-100 mt-3 d-none d-lg-flex justify-content-center"
                >
                  <svg class="shopee-logo-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1a5.25 5.25 0 0 0-5.224 5.78H4.5A1.5 1.5 0 0 0 3 8.28v12A1.5 1.5 0 0 0 4.5 21.78h15a1.5 1.5 0 0 0 1.5-1.5v-12a1.5 1.5 0 0 0-1.5-1.5h-2.276A5.25 5.25 0 0 0 12 1zm0 1.5a3.75 3.75 0 0 1 3.718 4.28H8.282A3.75 3.75 0 0 1 12 2.5zM4.5 8.28H19.5v12H4.5v-12zm7.5 2.25a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0 1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"/>
                  </svg>
                  <span>Mua trên Shopee</span>
                  <icon icon="fa-solid fa-arrow-up-right-from-square" class="shopee-ext-icon" />
                </a>
              </div>

              <!-- Suggested / Related Books Sidebar (Desktop) -->
              <div class="related-books-card mt-4" v-if="relatedBooks && relatedBooks.length > 0">
                <div class="related-header">
                  <h3 class="related-title m-0">Tác phẩm cùng chủ đề</h3>
                </div>
                <div class="related-list">
                  <router-link
                    v-for="rel in relatedBooks"
                    :key="rel.id"
                    :to="'/book/' + rel.id"
                    class="related-item"
                  >
                    <div class="related-cover-box">
                      <img
                        :src="formatCoverUrl(rel.cover)"
                        :alt="rel.name"
                        class="related-img"
                        loading="lazy"
                      />
                    </div>
                    <div class="related-meta-box">
                      <h4 class="related-name" :title="rel.name">{{ rel.name }}</h4>
                      <div class="related-author" v-if="formatRelatedAuthors(rel.authorsStr)">
                        {{ formatRelatedAuthors(rel.authorsStr) }}
                      </div>
                      <div class="related-footer">
                        <div class="related-score">
                          <icon icon="fa-solid fa-star" class="star-mini" />
                          <span>{{ (rel.evaluate !== null && rel.evaluate !== undefined) ? rel.evaluate.toFixed(1) : '0.0' }}</span>
                        </div>
                        <div class="related-category-pill" v-if="formatRelatedCategory(rel.categoriesStr)">
                          {{ formatRelatedCategory(rel.categoriesStr) }}
                        </div>
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Ghi chú & Tiến độ cá nhân Modal -->
      <div v-if="showNoteModal" class="custom-modal-backdrop">
        <div class="custom-modal-card animate-slide-up">
          <div class="custom-modal-header d-flex align-items-center justify-content-between">
            <h4 class="custom-modal-title font-serif fw-bold m-0">
              <icon icon="fa-solid fa-book-bookmark" class="me-2 text-primary-color" />
              Ghi chú & Tiến độ cá nhân
            </h4>
            <button @click="closeBookNoteModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="custom-modal-body">
            <div v-if="isLoadingNote" class="text-center py-5">
              <icon icon="fa-solid fa-spinner" class="fa-spin text-primary-color fs-3" />
              <p class="text-muted mt-2 fs-7">Đang tải dữ liệu ghi chú...</p>
            </div>

            <div v-else>
              <div class="row g-4 mb-4">
                <!-- Trạng thái đọc -->
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2 section-collapse-header" @click="toggleReadingStatus">
                    <icon :icon="collapseReadingStatus ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-down'" class="section-collapse-icon" />
                    <label class="form-label fw-bold text-secondary fs-7 m-0">Trạng thái đọc</label>
                  </div>
                  <transition name="collapse-fade">
                    <div v-show="isMobile ? !collapseReadingStatus : true" class="status-options-grid">
                      <button 
                        type="button"
                        v-for="status in progressStatuses" 
                        :key="status.value"
                        :class="['btn-status-pill', 'status-' + status.value, { 'active': bookNote.progressStatus === status.value }]"
                        @click="selectProgressStatus(status.value)"
                      >
                        <icon :icon="status.icon" />
                        {{ status.label }}
                      </button>
                    </div>
                  </transition>
                </div>

                <!-- Trạng thái sở hữu -->
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2 section-collapse-header" @click="toggleOwnership">
                    <icon :icon="collapseOwnership ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-down'" class="section-collapse-icon" />
                    <label class="form-label fw-bold text-secondary fs-7 m-0">Sở hữu sách</label>
                  </div>
                  <transition name="collapse-fade">
                    <div v-show="isMobile ? !collapseOwnership : true" class="status-options-grid">
                      <button 
                        type="button"
                        v-for="own in ownStatuses" 
                        :key="own.value"
                        :class="['btn-own-pill', { 'active': bookNote.ownStatus === own.value }]"
                        @click="bookNote.ownStatus = own.value"
                      >
                        <icon :icon="own.icon" />
                        {{ own.label }}
                      </button>
                    </div>
                  </transition>
                </div>
              </div>

              <!-- Tiến độ đọc (số trang / %) - Only visible when reading (2) or paused (4) -->
              <div v-if="bookNote.progressStatus === 2 || bookNote.progressStatus === 4" class="progress-edit-section border-top pt-4 mb-4">
                <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <label class="form-label fw-bold text-secondary fs-7 m-0">Tiến độ đọc</label>
                  
                  <!-- Toggle Type: Trang vs Phần trăm -->
                  <div class="progress-type-toggle">
                    <button 
                      type="button"
                      :class="['btn-toggle-type', { 'active': !bookNote.type }]"
                      @click="setNoteType(false)"
                    >
                      Theo số trang
                    </button>
                    <button 
                      type="button"
                      :class="['btn-toggle-type', { 'active': bookNote.type }]"
                      @click="setNoteType(true)"
                    >
                      Theo phần trăm (%)
                    </button>
                  </div>
                </div>

                <div class="row g-3 align-items-end">
                  <!-- Input số trang -->
                  <template v-if="!bookNote.type">
                    <div class="col-6 col-sm-4">
                      <label class="fs-8 text-muted mb-1 d-block">Trang hiện tại</label>
                      <input 
                        type="number" 
                        class="form-control progress-input-number" 
                        v-model.number="bookNote.currentPage"
                        min="0"
                        :max="bookNote.totalPage"
                        @input="calculatePercent"
                      />
                    </div>
                    <div class="col-6 col-sm-4">
                      <label class="fs-8 text-muted mb-1 d-block">Tổng số trang</label>
                      <input 
                        type="number" 
                        class="form-control progress-input-number" 
                        v-model.number="bookNote.totalPage"
                        min="0"
                        @input="calculatePercent"
                      />
                    </div>
                  </template>

                  <!-- Input % -->
                  <template v-else>
                    <div class="col-12 col-sm-8">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <label class="fs-8 text-muted">Phần trăm hoàn thành</label>
                      </div>
                      <div class="d-flex align-items-center gap-3">
                        <input 
                          type="range" 
                          class="form-range progress-slider flex-grow-1" 
                          v-model.number="bookNote.percent"
                          @input="onPercentInput"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <div class="percent-input-box">
                          <input 
                            type="number" 
                            class="percent-input-field" 
                            v-model.number="bookNote.percent"
                            @input="onPercentInput"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0"
                          />
                          <span class="percent-badge-unit">%</span>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Visual progress bar -->
                  <div class="col-12 mt-3">
                    <div class="custom-progress-track">
                      <div 
                        class="custom-progress-bar" 
                        :style="{ width: visualPercent + '%' }"
                      ></div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mt-1.5 fs-8 text-muted">
                      <span>Tiến độ: <strong class="text-primary-color">{{ visualPercent }}%</strong></span>
                      <span v-if="bookNote.totalPage > 0">{{ bookNote.currentPage }} / {{ bookNote.totalPage }} trang</span>
                    </div>
                  </div>

                  <!-- Suggest chuyển sang Đã đọc xong khi đọc hết trang -->
                  <div v-if="bookNote.progressStatus === 2 && !bookNote.type && bookNote.totalPage > 0 && bookNote.currentPage >= bookNote.totalPage" class="suggest-read-badge d-flex align-items-center gap-2 mt-3 mb-0">
                    <icon icon="fa-solid fa-circle-check" />
                    <span>Bạn đã đọc hết sách! Nhấn <strong @click="selectProgressStatus(3)">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                  </div>
                  <div v-if="bookNote.progressStatus === 2 && bookNote.type && bookNote.percent >= 100" class="suggest-read-badge d-flex align-items-center gap-2 mt-3 mb-0">
                    <icon icon="fa-solid fa-circle-check" />
                    <span>Bạn đã đọc hết sách! Nhấn <strong @click="selectProgressStatus(3)">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                  </div>
                </div>
              </div>

              <!-- Ghi chú cá nhân -->
              <div v-if="!showNoteTextarea" class="note-toggle-trigger text-center py-2 mb-4 border-top pt-4">
                <button type="button" class="btn-toggle-note-textarea" @click="openNoteTextarea">
                  <icon :icon="hasExistingNote ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" class="me-1.5" />
                  {{ hasExistingNote ? 'Xem / Chỉnh sửa ghi chú cá nhân' : 'Viết ghi chú cá nhân (tùy chọn)' }}
                </button>
              </div>

              <div v-else class="note-textarea-section border-top pt-4 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold text-secondary fs-7 m-0">
                    Ghi chú của bạn
                    <button type="button" class="btn-hide-note-textarea ms-2 text-muted" @click="hideNoteTextarea" title="Ẩn ghi chú">
                      (Ẩn đi)
                    </button>
                  </label>
                  <span class="fs-8 text-muted">{{ bookNote.note ? bookNote.note.length : 0 }} / 500 ký tự</span>
                </div>
                <textarea 
                  class="form-control note-textarea" 
                  rows="4" 
                  placeholder="Nhập ghi chú cá nhân của bạn tại đây (ví dụ: cảm nghĩ, trích dẫn tâm đắc, bài học rút ra từ cuốn sách...)"
                  v-model="bookNote.note"
                  maxlength="500"
                ></textarea>
              </div>

              <!-- Validation Alert helper -->
              <div v-if="isDeleteAction" class="alert alert-warning py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-triangle-exclamation" class="text-warning" />
                <span>Lưu ý: Chọn <strong>Chưa đọc</strong> và <strong>Chưa sở hữu</strong> sẽ xóa cuốn sách này khỏi tủ sách cá nhân, đồng thời xóa nhận xét và đánh giá của bạn (nếu có). Các bình luận và trả lời trong thảo luận sẽ không bị xóa.</span>
              </div>
              <div v-else-if="hadPreviousBookData && (Number(bookNote.progressStatus) === 0 || Number(bookNote.progressStatus) === 1)" class="alert alert-info py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-circle-info" class="text-info" />
                <span>Lưu ý: Chọn <strong>{{ Number(bookNote.progressStatus) === 0 ? 'Chưa đọc' : 'Muốn đọc' }}</strong> sẽ tự động xóa nhận xét và đánh giá của bạn (nếu có). Các bình luận và trả lời trong thảo luận sẽ không bị xóa.</span>
              </div>
              <div v-else-if="noteValidationError" class="alert alert-danger py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-circle-exclamation" class="text-danger" />
                <span>{{ noteValidationError }}</span>
              </div>
            </div>
          </div>

          <div class="custom-modal-footer d-flex justify-content-end gap-2 border-top pt-3">
            <button type="button" class="btn-cancel-note" @click="closeBookNoteModal" :disabled="isSavingNote">
              Hủy bỏ
            </button>
            <button 
              type="button" 
              :class="['btn-save-note', { 'btn-danger bg-danger text-white border-danger': isDeleteAction }]" 
              :disabled="!isNoteModified || isSavingNote || !!noteValidationError" 
              @click="saveBookNote"
            >
              <icon v-if="isSavingNote" icon="fa-solid fa-spinner" class="fa-spin" />
              <icon v-else-if="isDeleteAction" icon="fa-solid fa-trash-can" />
              <icon v-else icon="fa-solid fa-floppy-disk" />
              <span>{{ isSavingNote ? (isDeleteAction ? 'Đang xóa...' : 'Đang lưu...') : (isDeleteAction ? 'Xóa khỏi tủ sách' : 'Lưu ghi chú & tiến độ') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Custom Delete Confirmation Modal -->
      <transition name="modal-fade">
        <div
          v-if="deleteConfirmModal.show"
          class="custom-modal-backdrop d-flex align-items-center justify-content-center"
          @click.self="closeDeleteModal"
        >
          <div class="delete-confirm-card animate-scale-up" role="dialog" aria-modal="true">
            <div class="delete-confirm-header">
              <div class="delete-icon-wrapper">
                <icon icon="fa-solid fa-trash-can" />
              </div>
              <h4 class="delete-confirm-title">{{ deleteConfirmModal.title }}</h4>
            </div>
            
            <div class="delete-confirm-body">
              <p class="delete-confirm-message">{{ deleteConfirmModal.message }}</p>
            </div>

            <div class="delete-confirm-actions">
              <button
                type="button"
                class="btn-delete-cancel"
                @click="closeDeleteModal"
                :disabled="deleteConfirmModal.isDeleting"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                class="btn-delete-confirm"
                @click="executeDelete"
                :disabled="deleteConfirmModal.isDeleting"
              >
                <icon v-if="deleteConfirmModal.isDeleting" icon="fa-solid fa-spinner" class="fa-spin me-1.5" />
                <icon v-else icon="fa-solid fa-trash-can" class="me-1.5" />
                <span>{{ deleteConfirmModal.isDeleting ? 'Đang xóa...' : 'Xác nhận xóa' }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Custom Rating Change Confirmation Modal -->
      <transition name="modal-fade">
        <div
          v-if="ratingConfirmModal.show"
          class="custom-modal-backdrop d-flex align-items-center justify-content-center"
          @click.self="closeRatingConfirmModal"
        >
          <div class="delete-confirm-card animate-scale-up" role="dialog" aria-modal="true">
            <div class="delete-confirm-header">
              <div class="rating-icon-wrapper">
                <icon icon="fa-solid fa-star" />
              </div>
              <h4 class="delete-confirm-title">Xác nhận thay đổi đánh giá</h4>
            </div>
            
            <div class="delete-confirm-body">
              <p class="delete-confirm-message">
                Bạn đã đánh giá cuốn sách này <strong>{{ ratingConfirmModal.oldRating }} sao</strong>.
                Bạn có chắc chắn muốn thay đổi đánh giá thành <strong>{{ ratingConfirmModal.pendingRating }} sao</strong> không?
              </p>
            </div>

            <div class="delete-confirm-actions">
              <button
                type="button"
                class="btn-delete-cancel"
                @click="closeRatingConfirmModal"
                :disabled="ratingConfirmModal.isSubmitting"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                class="btn-rating-confirm"
                @click="confirmRatingChange"
                :disabled="ratingConfirmModal.isSubmitting"
              >
                <icon v-if="ratingConfirmModal.isSubmitting" icon="fa-solid fa-spinner" class="fa-spin me-1.5" />
                <icon v-else icon="fa-solid fa-check" class="me-1.5" />
                <span>{{ ratingConfirmModal.isSubmitting ? 'Đang lưu...' : 'Xác nhận' }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- After Rate: Ask user to write a review -->
      <transition name="modal-fade">
        <div
          v-if="afterRateReviewModal.show"
          class="custom-modal-backdrop d-flex align-items-center justify-content-center"
          @click.self="closeAfterRateModal"
        >
          <div class="delete-confirm-card animate-scale-up" role="dialog" aria-modal="true">
            <div class="delete-confirm-header">
              <div class="rating-icon-wrapper">
                <icon icon="fa-solid fa-pen-fancy" />
              </div>
              <h4 class="delete-confirm-title">Viết nhận xét</h4>
            </div>

            <div class="delete-confirm-body">
              <p class="delete-confirm-message">
                Bạn có muốn viết nhận xét về cuốn sách này không?
              </p>
            </div>

            <div class="delete-confirm-actions">
              <button
                type="button"
                class="btn-delete-cancel"
                @click="closeAfterRateModal"
              >
                Để sau
              </button>
              <button
                type="button"
                class="btn-rating-confirm"
                @click="goToWriteReviewFromModal"
              >
                <icon icon="fa-solid fa-pen" class="me-1.5" />
                <span>Viết ngay</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- After Review: Ask user to rate stars -->
      <transition name="modal-fade">
        <div
          v-if="afterReviewRateModal.show"
          class="custom-modal-backdrop d-flex align-items-center justify-content-center"
          @click.self="closeAfterReviewModal"
        >
          <div class="delete-confirm-card animate-scale-up" role="dialog" aria-modal="true">
            <div class="delete-confirm-header">
              <div class="rating-icon-wrapper">
                <icon icon="fa-solid fa-star" />
              </div>
              <h4 class="delete-confirm-title">Đánh giá cuốn sách</h4>
            </div>

            <div class="delete-confirm-body">
              <p class="delete-confirm-message">
                Bạn muốn đánh giá mấy sao cho cuốn sách này?
              </p>
              <div class="after-action-star-picker">
                <button
                  v-for="s in 5"
                  :key="s"
                  type="button"
                  class="star-interactive-btn"
                  @mouseover="afterReviewRateModal.hoverRating = s"
                  @mouseleave="afterReviewRateModal.hoverRating = 0"
                  @click="afterReviewRateModal.selectedRating = s"
                  :title="'Đánh giá ' + s + ' sao'"
                >
                  <icon
                    icon="fa-solid fa-star"
                    class="star-interactive-icon"
                    :class="s <= (afterReviewRateModal.hoverRating || afterReviewRateModal.selectedRating) ? 'star-active' : 'star-inactive'"
                  />
                </button>
              </div>
            </div>

            <div class="delete-confirm-actions">
              <button
                type="button"
                class="btn-delete-cancel"
                @click="closeAfterReviewModal"
                :disabled="isSubmittingRating"
              >
                Bỏ qua
              </button>
              <button
                type="button"
                class="btn-rating-confirm"
                @click="submitRatingFromReviewModal"
                :disabled="!afterReviewRateModal.selectedRating || isSubmittingRating"
              >
                <icon v-if="isSubmittingRating" icon="fa-solid fa-spinner" class="fa-spin me-1.5" />
                <icon v-else icon="fa-solid fa-check" class="me-1.5" />
                <span>{{ isSubmittingRating ? 'Đang lưu...' : 'Lưu' }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- GLOBAL EMOJI & STICKER PICKER POPOVER -->
      <transition name="fade-slide">
        <div v-if="showEmojiPicker" class="emoji-sticker-picker-popover" :style="emojiPickerPos" @click.stop>
          <div class="picker-header">
            <div class="picker-tabs">
              <button type="button" :class="['btn-picker-tab', { active: emojiPickerTab === 'emojis' }]" @click="emojiPickerTab = 'emojis'">
                <span>😃 Biểu cảm</span>
              </button>
              <button type="button" :class="['btn-picker-tab', { active: emojiPickerTab === 'stickers' }]" @click="emojiPickerTab = 'stickers'">
                <span>🐸 Nhãn dán hài hước</span>
              </button>
            </div>
            <button type="button" class="btn-close-picker" title="Đóng" @click="closeEmojiPicker">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <!-- Picker Search Bar -->
          <div class="picker-search-wrapper">
            <icon icon="fa-solid fa-magnifying-glass" class="picker-search-icon" />
            <input
              v-model="emojiPickerSearch"
              type="text"
              class="picker-search-input"
              :placeholder="emojiPickerTab === 'stickers' ? 'Tìm nhãn dán GIF (VD: Mèo, Pepe, Khóc)...' : 'Tìm biểu cảm emoji...'"
            />
            <button v-if="emojiPickerSearch" type="button" class="btn-clear-search" @click="emojiPickerSearch = ''">
              <icon icon="fa-solid fa-circle-xmark" />
            </button>
          </div>

          <!-- Emojis Tab Content -->
          <div v-if="emojiPickerTab === 'emojis'" class="picker-body">
            <div v-for="(cat, idx) in emojiCategories" :key="'cat-' + idx" class="emoji-cat-section">
              <div class="cat-title">
                <span>{{ cat.icon }} {{ cat.name }}</span>
              </div>
              <div class="emoji-grid">
                <button v-for="(emoji, eIdx) in cat.list" :key="'e-' + eIdx" type="button" class="btn-emoji-item" @click="insertEmoji(emoji)">
                  {{ emoji }}
                </button>
              </div>
            </div>
          </div>

          <!-- Funny Stickers Tab Content -->
          <div v-else class="picker-body stickers-body">
            <div class="animated-stickers-grid">
              <button v-for="st in filteredStickers" :key="st.id" type="button" class="btn-animated-sticker-card" @click="insertSticker(st)">
                <div class="sticker-gif-thumb">
                  <img :src="st.url" :alt="st.name" />
                </div>
                <span class="sticker-gif-name">{{ st.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

    </main>

    <Footer />
  </div>


    <!-- Review Focus Modal -->
    <div class="modal fade" id="reviewFocusModal" tabindex="-1" aria-labelledby="reviewFocusModalLabel" data-bs-backdrop="static" data-bs-keyboard="false" ref="reviewFocusModal">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content border-0">
          <div class="review-focus-header">
            <div class="d-flex align-items-center gap-2">
              <span class="modal-eyebrow-tag">
                <icon icon="fa-solid fa-wand-magic-sparkles" /> CHI TIẾT NHẬN XÉT & THẢO LUẬN
              </span>
            </div>
            <button type="button" class="btn-modal-close-pill" data-bs-dismiss="modal" aria-label="Đóng" title="Đóng cửa sổ">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body p-3 p-md-4">
            <div v-if="focusedReview" class="container-fluid p-0">
              <div class="row g-4">
                <!-- Left Pane: Review Hero & Details -->
                <div class="col-lg-6">
                  <article class="community-review-card focus-mode mb-0">
                    <!-- Reviewer identity row -->
                    <div class="d-flex align-items-center gap-3 my-review-identity">
                      <div class="review-entry-avatar flex-shrink-0" :style="getAvatarGradientStyle(focusedReview.reviewerName)">
                        <img v-if="getAvatarSrc(focusedReview.avatar)" :src="getAvatarSrc(focusedReview.avatar)" alt="Avatar" class="avatar-img" />
                        <template v-else>{{ focusedReview.reviewerName ? focusedReview.reviewerName.charAt(0).toUpperCase() : 'U' }}</template>
                      </div>
                      <div class="my-review-identity-info">
                        <div class="d-flex align-items-center flex-wrap gap-1">
                          <strong class="review-entry-name text-break">{{ focusedReview.reviewerName }}</strong>
                          <span v-if="focusedReview.reviewerRating > 0" class="review-rating-stars-mini" :title="'Đã đánh giá ' + focusedReview.reviewerRating + '/5 sao'">
                            <icon v-for="s in 5" :key="s" icon="fa-solid fa-star" class="mini-star-icon" :class="{ 'mini-star-filled': s <= focusedReview.reviewerRating }" />
                          </span>
                        </div>
                        <span v-if="focusedReview.createTime" class="review-time fs-9 text-muted">
                          {{ formatCommentTime(focusedReview.createTime) }}
                          <span v-if="focusedReview.isEdited || focusedReview.edited" class="is-edited-badge ms-1.5">(Đã chỉnh sửa)</span>
                        </span>
                      </div>
                    </div>

                    <!-- Content -->
                    <div v-if="focusedReview.isSpoil && !isSpoilerRevealed(focusedReview.id)" class="spoil-guard mt-2" @click="toggleSpoiler(focusedReview.id)" role="button" tabindex="0">
                      <icon icon="fa-solid fa-lock" class="spoil-guard-icon" />
                      <span>Nội dung có spoiler — click để xem</span>
                    </div>
                    <div v-else class="my-review-content-wrap">
                      <div v-if="focusedReview.isSpoil" class="my-review-spoil-corner">
                        <span class="rbadge rbadge-spoil"><icon icon="fa-solid fa-eye-slash" /> Spoiler</span>
                        <button @click="toggleSpoiler(focusedReview.id)" class="btn-hide-again">Ẩn lại</button>
                      </div>
                      <p class="my-review-text" v-html="formatContentHtml(focusedReview.content)"></p>
                      <div v-if="focusedReview.readAgain" class="my-review-content-badges mt-1">
                        <span class="rbadge rbadge-reread"><icon icon="fa-solid fa-arrows-rotate" /> Đọc lại</span>
                      </div>
                    </div>

                    <!-- Actions footer -->
                    <div class="my-review-actions">
                      <div class="tooltip-wrapper">
                        <button @click="interactReview(focusedReview, true)" class="btn-review-action btn-like-toggle" :class="{ 'active': focusedReview.userInteract === 1 }">
                          <icon icon="fa-solid fa-thumbs-up" /><span>{{ focusedReview.likeCount || 0 }}</span>
                        </button>
                        <span class="custom-tooltip-text">Thích</span>
                      </div>
                      <div class="tooltip-wrapper">
                        <button @click="interactReview(focusedReview, false)" class="btn-review-action btn-dislike-toggle" :class="{ 'active': focusedReview.userInteract === 0 }">
                          <icon icon="fa-solid fa-thumbs-down" /><span>{{ focusedReview.dislikeCount || 0 }}</span>
                        </button>
                        <span class="custom-tooltip-text">Không thích</span>
                      </div>
                      <div class="tooltip-wrapper">
                        <button @click="copyReviewUrl(focusedReview.id)" class="btn-review-action btn-share-review" title="Sao chép liên kết nhận xét">
                          <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                        </button>
                        <span class="custom-tooltip-text">Sao chép liên kết</span>
                      </div>
                    </div>
                  </article>
                </div>

                <!-- Right Pane: Dedicated Comments Drawer -->
                <div class="col-lg-6">
                  <div class="focus-comments-pane">
                    <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-light-subtle">
                      <div class="d-flex align-items-center gap-2">
                        <icon icon="fa-solid fa-comments" class="text-primary fs-6" />
                        <span class="fw-bold fs-7 text-dark">Bình luận & Thảo luận</span>
                      </div>
                      <span class="badge rounded-pill bg-primary-subtle text-primary fw-semibold px-2.5 py-1 fs-9">
                        {{ commentsMap[focusedReview.id] ? commentsMap[focusedReview.id].length : (focusedReview.commentCount || 0) }} bình luận
                      </span>
                    </div>

                    <!-- Scrollable Comments List -->
                    <div class="focus-comments-scroll-area mb-3" :data-review-comments="focusedReview.id">
                      <div v-if="isCommentsLoadingMap[focusedReview.id] && (!commentsMap[focusedReview.id] || commentsMap[focusedReview.id].length === 0)" class="comments-loading text-center py-4">
                        <icon icon="fa-solid fa-spinner" class="fa-spin text-primary fs-5" />
                        <p class="fs-9 text-muted mt-2">Đang tải bình luận...</p>
                      </div>
                      <div v-else class="comments-list">
                        <div v-if="!commentsMap[focusedReview.id] || commentsMap[focusedReview.id].length === 0" class="comment-item-empty text-muted py-4 fs-8 text-center bg-white rounded-3 border border-light-subtle">
                          Chưa có bình luận nào. Hãy gửi bình luận đầu tiên!
                        </div>
                        <div v-else v-for="comment in commentsMap[focusedReview.id]" :key="comment.id" :data-comment-id="comment.id" :data-user-id="comment.userId" class="comment-item mb-2 p-2.5 bg-white rounded-3 border border-light-subtle shadow-sm">
                          <div class="comment-avatar" :style="getAvatarGradientStyle(comment.userName)">
                            <img v-if="getAvatarSrc(comment.avatar)" :src="getAvatarSrc(comment.avatar)" alt="Avatar" class="avatar-img" />
                            <template v-else>{{ comment.userName ? comment.userName.charAt(0).toUpperCase() : 'U' }}</template>
                          </div>
                          <div class="comment-body">
                            <div class="comment-header-row">
                              <div class="comment-header-info">
                                <strong class="comment-author fs-8 text-dark">{{ comment.userName }}</strong>
                                <span class="comment-meta-inline">
                                  <span class="comment-time fs-9 text-muted">{{ formatCommentTime(comment.createTime) }}</span>
                                  <span v-if="comment.isEdited || comment.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                </span>
                              </div>
                              <div v-if="isLoggedIn && currentUserId && String(comment.userId) === String(currentUserId) && !isEditingCommentMap[comment.id]" class="comment-top-right-actions">
                                <button @click="editComment(comment)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                  <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                </button>
                                <button @click="confirmDeleteComment(focusedReview.id, comment.id)" class="btn-comment-action btn-delete-comment" title="Xóa bình luận">
                                  <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                </button>
                              </div>
                            </div>
                            <p v-if="!isEditingCommentMap[comment.id]" class="comment-content m-0 fs-8-5 text-secondary" v-html="formatContentHtml(comment.content)"></p>
                            <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                              <div v-if="selectedStickersMap['editComment_' + comment.id] && selectedStickersMap['editComment_' + comment.id].length > 0" class="d-block mb-2">
                                <div v-for="(sticker, sIndex) in selectedStickersMap['editComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                  <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                  <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                    <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                  </span>
                                  <button type="button" @click="removeSelectedSticker('editComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                    <icon icon="fa-solid fa-xmark" />
                                  </button>
                                </div>
                              </div>
                              <textarea
                                v-model="editingCommentMap[comment.id]"
                                :data-edit-comment-input="comment.id"
                                class="comment-edit-input"
                                rows="2"
                                placeholder="Nhập nội dung bình luận..."
                                maxlength="500"
                              ></textarea>
                              <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                <span class="fs-9 text-muted">{{ (editingCommentMap[comment.id] || '').length }}/500</span>
                                <div class="d-flex gap-2">
                                  <button type="button" @click="cancelEditComment(comment.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[comment.id]">Hủy</button>
                                  <button type="button" @click="updateCommentContent(focusedReview.id, comment)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[comment.id] || !isCommentChanged(comment)">
                                    <icon v-if="isSubmittingEditCommentMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                    <span>Lưu</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div v-if="!isEditingCommentMap[comment.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                              <button @click="interactComment(comment, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': comment.userInteract === 1 }" title="Thích">
                                <icon icon="fa-solid fa-thumbs-up" /><span>{{ comment.likeCount || 0 }}</span>
                              </button>
                              <button @click="interactComment(comment, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': comment.userInteract === 0 }" title="Không thích">
                                <icon icon="fa-solid fa-thumbs-down" /><span>{{ comment.dislikeCount || 0 }}</span>
                              </button>
                              <button @click="toggleReplyForm(focusedReview.id, comment.id)" class="btn-comment-action btn-reply-comment" :class="{ 'active': showReplyFormsMap[comment.id] }" title="Trả lời">
                                <icon icon="fa-solid fa-reply" /><span>Trả lời</span>
                              </button>
                              <button @click="copyCommentUrl(focusedReview.id, comment.id, 'COMMENT')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết bình luận">
                                <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                              </button>
                            </div>
                              <!-- Nested replies toggle (inside comment-body) -->
                              <div v-if="comment.replies && comment.replies.length > 0" class="comment-replies-toggle">
                                <button @click="toggleReplies(comment.id)" class="btn-toggle-replies">
                                  <icon :icon="showRepliesMap[comment.id] ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="toggle-reply-icon" />
                                  <span>{{ showRepliesMap[comment.id] ? 'Ẩn' : 'Xem' }} {{ comment.replies.length }} câu trả lời</span>
                                </button>
                              </div>
                              <!-- Nested replies & reply form thread container (inside comment-body) -->
                              <div v-if="(showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0) || showReplyFormsMap[comment.id]" class="comment-replies" :data-replies-list="comment.id">
                                <!-- Existing replies list -->
                                <div v-if="showRepliesMap[comment.id] && comment.replies && comment.replies.length > 0" class="comment-replies-list">
                                  <div v-for="reply in comment.replies" :key="reply.id" class="comment-reply-item" :data-reply-user-id="reply.userId">
                                    <div class="comment-reply-avatar" :style="getAvatarGradientStyle(reply.userName)">
                                      <img v-if="getAvatarSrc(reply.avatar)" :src="getAvatarSrc(reply.avatar)" alt="Avatar" class="avatar-img" />
                                      <template v-else>{{ reply.userName ? reply.userName.charAt(0).toUpperCase() : 'U' }}</template>
                                    </div>
                                    <div class="comment-reply-body">
                                      <div class="comment-header-row">
                                        <div class="comment-header-info">
                                          <strong class="comment-author text-dark">{{ reply.userName }}</strong>
                                          <span class="comment-meta-inline">
                                            <span class="comment-time text-muted">{{ formatCommentTime(reply.createTime) }}</span>
                                            <span v-if="reply.isEdited || reply.edited" class="is-edited-badge">(Đã chỉnh sửa)</span>
                                          </span>
                                        </div>
                                        <div v-if="isLoggedIn && currentUserId && String(reply.userId) === String(currentUserId) && !isEditingCommentMap[reply.id]" class="comment-top-right-actions">
                                          <button @click="editComment(reply)" class="btn-comment-action btn-edit-comment" title="Chỉnh sửa">
                                            <icon icon="fa-solid fa-pen-to-square" /><span class="action-btn-text">Sửa</span>
                                          </button>
                                          <button @click="confirmDeleteComment(focusedReview.id, reply.id)" class="btn-comment-action btn-delete-comment" title="Xóa câu trả lời">
                                            <icon icon="fa-solid fa-trash-can" /><span class="action-btn-text">Xóa</span>
                                          </button>
                                        </div>
                                      </div>
                                      <p v-if="!isEditingCommentMap[reply.id]" class="comment-content m-0 text-secondary" v-html="formatContentHtml(reply.content)"></p>
                                      <div v-else class="comment-edit-compact my-1.5 animate-fade-in">
                                        <textarea
                                          v-model="editingCommentMap[reply.id]"
                                          :data-edit-comment-input="reply.id"
                                          class="comment-edit-input"
                                          rows="2"
                                          placeholder="Nhập câu trả lời..."
                                          maxlength="500"
                                        ></textarea>
                                        <div class="d-flex justify-content-between align-items-center gap-2 mt-1.5">
                                          <span class="fs-9 text-muted">{{ (editingCommentMap[reply.id] || '').length }}/500</span>
                                          <div class="d-flex gap-2">
                                            <button type="button" @click="cancelEditComment(reply.id)" class="btn-comment-cancel" :disabled="isSubmittingEditCommentMap[reply.id]">Hủy</button>
                                            <button type="button" @click="updateCommentContent(focusedReview.id, reply)" class="btn-comment-save" :disabled="isSubmittingEditCommentMap[reply.id] || !isCommentChanged(reply)">
                                              <icon v-if="isSubmittingEditCommentMap[reply.id]" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                                              <span>Lưu</span>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div v-if="!isEditingCommentMap[reply.id]" class="comment-actions-row d-flex align-items-center gap-2 mt-1">
                                        <button @click="interactComment(reply, true)" class="btn-comment-action btn-like-comment" :class="{ 'active': reply.userInteract === 1 }" title="Thích">
                                          <icon icon="fa-solid fa-thumbs-up" /><span>{{ reply.likeCount || 0 }}</span>
                                        </button>
                                        <button @click="interactComment(reply, false)" class="btn-comment-action btn-dislike-comment" :class="{ 'active': reply.userInteract === 0 }" title="Không thích">
                                          <icon icon="fa-solid fa-thumbs-down" /><span>{{ reply.dislikeCount || 0 }}</span>
                                        </button>
                                        <button @click="copyCommentUrl(focusedReview.id, reply.id, 'REPLY')" class="btn-comment-action btn-share-comment" title="Sao chép liên kết câu trả lời">
                                          <icon icon="fa-solid fa-link" /><span>Sao chép liên kết</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <!-- Inline reply form inside the same thread container -->
                                <div v-if="showReplyFormsMap[comment.id]" class="comment-reply-form mt-2 animate-fade-in">
                                  <div v-if="selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0" class="d-block mb-2">
                                  <div v-for="(sticker, sIndex) in selectedStickersMap['replyComment_' + comment.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                                    <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                                    <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                      <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                                    </span>
                                    <button type="button" @click="removeSelectedSticker('replyComment_' + comment.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                      <icon icon="fa-solid fa-xmark" />
                                    </button>
                                  </div>
                                  </div>
                                <form @submit.prevent="submitReply(focusedReview.id, comment.id)" class="comment-form-flex">
                                    <textarea v-model="replyInputsMap[comment.id]" :data-reply-input="comment.id" class="comment-reply-input" :placeholder="'Trả lời ' + comment.userName + '...'" maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitReply(focusedReview.id, comment.id))" @input="autoResizeTextarea"></textarea>
                                    <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('replyComment_' + comment.id, $event)">
                                      <icon icon="fa-solid fa-face-smile" />
                                    </button>
                                    <button type="submit" class="btn-submit-comment" :disabled="submittingReplyMap[comment.id] || (!replyInputsMap[comment.id]?.trim() && !(selectedStickersMap['replyComment_' + comment.id] && selectedStickersMap['replyComment_' + comment.id].length > 0))" title="Gửi câu trả lời">
                                      <icon v-if="submittingReplyMap[comment.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                                      <icon v-else icon="fa-solid fa-paper-plane" />
                                    </button>
                                  </form>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="commentsTotalPagesMap[focusedReview.id] > 1" class="comments-mini-pagination d-flex justify-content-center align-items-center gap-2 mt-2 pt-2">
                            <button @click="fetchComments(focusedReview.id, commentsPageMap[focusedReview.id] - 1)" :disabled="commentsPageMap[focusedReview.id] === 0" class="btn-mini-pag">Trước</button>
                            <span class="fs-9 text-muted">{{ commentsPageMap[focusedReview.id] + 1 }} / {{ commentsTotalPagesMap[focusedReview.id] }}</span>
                            <button @click="fetchComments(focusedReview.id, commentsPageMap[focusedReview.id] + 1)" :disabled="commentsPageMap[focusedReview.id] === commentsTotalPagesMap[focusedReview.id] - 1" class="btn-mini-pag">Sau</button>
                          </div>
                        </div>
                        </div>
                        <div class="comment-form-wrapper mt-3 pt-2 border-top">
                          <div v-if="!isLoggedIn" class="comment-login-hint fs-8 text-muted">
                            Bạn cần <router-link to="/login" class="text-primary-color text-decoration-underline">đăng nhập</router-link> để gửi bình luận.
                          </div>
                          <form v-else @submit.prevent="submitComment(focusedReview.id)" class="d-flex flex-column gap-2 w-100">
                            <div v-if="selectedStickersMap['newComment_' + focusedReview.id] && selectedStickersMap['newComment_' + focusedReview.id].length > 0" class="d-block mb-2">
                            <div v-for="(sticker, sIndex) in selectedStickersMap['newComment_' + focusedReview.id]" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                              <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                              <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                                <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                              </span>
                              <button type="button" @click="removeSelectedSticker('newComment_' + focusedReview.id, sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                                <icon icon="fa-solid fa-xmark" />
                              </button>
                            </div>
                            </div>
                            <div class="comment-form-flex">
                              <textarea v-model="commentInputsMap[focusedReview.id]" class="comment-input-field" placeholder="Viết bình luận (tối đa 500 ký tự)..." maxlength="500" rows="1" @keydown="handleInputKeydown($event, () => submitComment(focusedReview.id))" @input="autoResizeTextarea"></textarea>
                            <button type="button" class="btn-emoji-trigger-icon" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('newComment_' + focusedReview.id, $event)">
                              <icon icon="fa-solid fa-face-smile" />
                            </button>
                            <button type="submit" class="btn-submit-comment" :disabled="isSubmittingCommentMap[focusedReview.id] || (!commentInputsMap[focusedReview.id]?.trim() && !(selectedStickersMap['newComment_' + focusedReview.id] && selectedStickersMap['newComment_' + focusedReview.id].length > 0))">
                              <icon v-if="isSubmittingCommentMap[focusedReview.id]" icon="fa-solid fa-spinner" class="fa-spin" />
                              <icon v-else icon="fa-solid fa-paper-plane" />
                            </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

</template>

<script src="./book-detail.js"></script>
<style scoped src="./book-detail.css"></style>
